import React, { useState } from 'react';

import styled from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { LoadingIndicator, Modal, PrimaryButton } from 'lib/components';
import CloseIcon from 'lib/images/CloseIcon';
import { theme } from 'lib/style';
import CalendarIcon from 'lib/images/CalendarIcon';
import { sendReportData } from 'lib/api';
import { RecipientsSelector } from './RecipientsSelector';
import { successToast } from 'lib/components/toasts/success';
import { RecurringModal } from './RecurringModal';

const steps = {
  RECIPIENTS: 'recipients',
  SUCCESS: 'success',
};

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 500px;
  display: flex;
  flex-direction: column;
  margin-top: 40px;

  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #272a32;
`;

const Footer = styled.div`
  margin-top: 32px;
`;

const ErrorMessage = styled.div`
  height: 20px;
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #e84c3d;
  padding-top: 4px;
`;

type Props = {
  handleModalClose: (showRecurring: boolean) => void;
  startDate: Date;
  endDate: Date;
  range: string;
  reportUrl?: string;
  reportId?: string;
  users?: string[];
  customer?: string;
  extraParams?: any;
  onError?: () => void;
  sendDataFunction?: (recipients: string[]) => Promise<void>;
  skipCreateRecurring?: boolean;
};

export const SendReportModal = (props: Props) => {
  const [step, setStep] = useState(steps.RECIPIENTS);
  const [recipients, setRecipients] = useState([] as string[]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRecurringModal, setShowRecurringModal] = useState(false);

  const {
    startDate,
    endDate,
    reportUrl,
    reportId,
    handleModalClose,
    range,
    users = [],
    customer,
    extraParams,
    onError,
    sendDataFunction,
    skipCreateRecurring = false,
  } = props;

  const { ref } = useOnClickOutside(handleModalClose);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose(false);
    }
  };
  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  const isRecipientStep = step === steps.RECIPIENTS;
  const isSuccessStep = step === steps.SUCCESS;

  const successMessage = 'The report was successfully sent!';

  const handleNewRecurringModalClose = () => {
    setShowRecurringModal(false);
    handleModalClose(false);
  };

  const sendReport = async () => {
    setLoading(true);
    try {
      let url = '';

      if (reportUrl) {
        url = reportUrl;
      }

      if (reportId) {
        url = `custom-reports/${reportId}`;
      }

      if (sendDataFunction) {
        await sendDataFunction(recipients);
        setLoading(false);
        handleModalClose(false);
        return;
      }
      await sendReportData(
        url,
        startDate,
        endDate,
        range,
        recipients,
        users,
        customer,
        extraParams,
        undefined,
        undefined
      );
    } catch (error) {
      console.log(error);
      if (onError) onError();
    }
    setLoading(false);
    setStep(steps.SUCCESS);
  };

  React.useEffect(() => {
    if (isSuccessStep && skipCreateRecurring) {
      successToast({ title: successMessage });
      handleModalClose(false);
    }
  }, [isSuccessStep]);

  return (
    <Modal>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>
            {isRecipientStep && 'Send Report'}
            {isSuccessStep && successMessage}
          </HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <CloseIcon
              onClick={() => handleModalClose(false)}
              width={24}
              height={24}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>

        <Content>
          {loading && <LoadingIndicator isLoading={loading} height='50px' />}

          {!loading && (
            <>
              {isRecipientStep && (
                <>
                  <RecipientsSelector
                    onChange={recipients => setRecipients(recipients)}
                    onError={email => {
                      if (email) {
                        setError('Invalid email address.');
                      } else {
                        setError('');
                      }
                    }}
                  />
                  {error && <ErrorMessage>{error}</ErrorMessage>}
                </>
              )}
              {!isRecipientStep && !skipCreateRecurring && (
                <div>
                  Do you want to create a recurring report? <br />
                  Data and recipients from this report will be already included.
                </div>
              )}
            </>
          )}
        </Content>
        <Footer>
          {!loading && (
            <>
              {isRecipientStep && (
                <PrimaryButton
                  text='Send'
                  textColor='white'
                  borderRadius={5}
                  fontSize='15px'
                  textPadding='8px 16px'
                  disabled={!recipients.length}
                  onClick={() => sendReport()}
                />
              )}
              {!isRecipientStep && !skipCreateRecurring && (
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <PrimaryButton
                    iconPadding={8}
                    textPadding='8px 16px 8px 0'
                    center={true}
                    text='Create Recurring'
                    backgroundColor={theme.palette.primaryDarkBlue10Percent}
                    textColor={theme.palette.black_1_100}
                    icon={<CalendarIcon color={theme.palette.black_1_100} />}
                    borderRadius={5}
                    fontSize='15px'
                    onClick={() => {
                      setShowRecurringModal(true);
                    }}
                  />
                  <PrimaryButton
                    text='Done'
                    textColor='white'
                    borderRadius={5}
                    fontSize='15px'
                    textPadding='8px 16px'
                    onClick={() => handleModalClose(false)}
                  />
                </div>
              )}
            </>
          )}
        </Footer>
        {reportId && showRecurringModal && (
          <RecurringModal
            reportId={reportId}
            handleModalClose={handleNewRecurringModalClose}
          />
        )}
      </div>
    </Modal>
  );
};
